'use client';
import { trackOrder } from '@/api/order';
import Image from '@/components/Image';
import MobileNav from '@/components/MobileNav';
import Popover from '@/components/Popover';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { toast } from '@/components/Toast';
import { colors } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { Box, CircularProgress, Drawer, Typography } from '@mui/material';
import { WapNavBackOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
// 判断值是否为空
const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
};

// API返回的跟踪数据结构
interface TrackData {
  [x: string]: any;
  order_ships?: any[];
  head_ships?: any[];
  order_node?: Array<{
    id?: number;
    content?: string;
    track_number?: string;
    created_at?: string;
    order_number?: string;
  }>;
  track_number?: string;
  shipment_number?: string;
  logistics_channel_href?: string;
  agent_logistics_code?: string;
  agent_code?: number;
  notice_message?: string;
  order_node_val?: any[];
}

export default function TrackOrder({
  orderId,
  orderNo,
  SDMinePageNavBtnClick = () => {},
  className,
  openTrackOrderDrawer = false,
  closeTrackOrderDrawer = () => {},
}: {
  orderId: number;
  orderNo: string;
  SDMinePageNavBtnClick?: (data: { module_name: string; btn_name: string }) => void;
  className?: string;
  openTrackOrderDrawer?: boolean;
  closeTrackOrderDrawer?: () => void;
}) {
  const t = useTranslations('account.trackOrder');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [trackMessage, setTrackMessage] = useState<TrackData | null>(null);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [noticeMessage, setNoticeMessage] = useState<string>('');

  // 计算要显示的跟踪消息数组
  const trackMessageArr = React.useMemo(() => {
    if (!trackMessage) return [];
    const { order_ships = [], head_ships = [], order_node_val = [] } = trackMessage;
    const arr = [...order_ships, ...head_ships, ...order_node_val];
    return arr.slice(0, 2);
  }, [trackMessage]);

  // 复制跟踪号
  const copyTrackNumber = () => {
    if (!trackMessage) return;

    const textToCopy = trackMessage.track_number || trackMessage.shipment_number || '';
    const textarea = document.createElement('textarea');
    textarea.value = textToCopy;
    document.body.appendChild(textarea);

    textarea.select();
    textarea.setSelectionRange(0, 99999); // 对于移动端兼容

    try {
      document.execCommand('copy');
      toast.success(t('copySuccess'));
    } catch (err) {
      logger.error('Failed to copy text: ', err);
    }

    document.body.removeChild(textarea);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    trackOrderAction();

    SDMinePageNavBtnClick({
      module_name: 'Recent Orders',
      btn_name: t('title'),
    });
  };

  const trackOrderAction = async () => {
    try {
      setShowLoading(true);
      const ret = await trackOrder({ order_id: orderId, page: 'order_detail_track' });

      if (ret.code === 200) {
        const data = ret.data || {};
        setNoticeMessage(data.notice_message || '');

        // 设置完整的跟踪消息对象
        setTrackMessage(data);
      }
    } catch (error) {
      logger.error('获取跟踪信息失败', error);
    } finally {
      setShowLoading(false);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'track-order-popover' : undefined;

  const MoblieTimeLine = ({ trackData = [] }: { trackData: any[] }) => {
    return (
      <div className="p-[0_12px_0_25px]">
        {trackData.map((item, index) => (
          <div
            key={item.created_at ? `${item.created_at} ${item.content}` : item.content}
            className="ml-[-5px] w-full"
          >
            <div className="flex justify-center items-center">
              <div
                className={`p-[9px_0]  bg-primary-white flex-0 z-1 leading-0 h-[27px] ${index === 0 ? 'self-start ml-[-1px]' : ''}`}
              >
                <div
                  className={`w-[9px] h-[9px] bg-[#c2c2c2] rounded-[50%] ${index === 0 ? 'w-[11px] h-[11px] bg-primary-purple' : ''}`}
                />
              </div>
              <div
                className={`ml-[-5px] pt-0 pl-[20px] w-full leading-[22px] border-l-1 border-[#c2c2c2] text-12 text-auxiliary-dark-gray ${index === 0 ? 'ml-[-6px]' : ''}`}
              >
                <div
                  className={
                    index === 0
                      ? 'pt-[0] pb-[12px] border-b-1 border-auxiliary-light-gray-blue text-primary-black'
                      : `pt-[12px] pb-[12px] ${index !== trackData.length - 1 ? 'border-b-1 border-auxiliary-light-gray-blue' : ''}`
                  }
                >
                  {item.created_at ? `${item.created_at} ${item.content}` : item.content}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (openTrackOrderDrawer) {
      trackOrderAction();
    }
  }, [openTrackOrderDrawer]);

  const trackOrderContent = () => {
    return (
      <div>
        {trackMessage && !isEmpty(trackMessage) ? (
          <Box>
            {noticeMessage && (
              <Box className="track_notice_message" sx={{ mb: 2 }}>
                {noticeMessage}
              </Box>
            )}
            <Box sx={{ position: 'relative', paddingLeft: '5%' }}>
              {showLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      color: '#333333',
                      mb: 2,
                    }}
                  >
                    {(trackMessage.track_number || trackMessage.shipment_number) && (
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {t('trackNumber')}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                            {trackMessage.track_number || trackMessage.shipment_number}
                          </Typography>
                          <Typography
                            onClick={copyTrackNumber}
                            sx={{
                              fontWeight: 'normal',
                              fontSize: '14px',
                              color: '#463AE8',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          >
                            {t('copy')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {trackMessage.logistics_channel_href && trackMessage.agent_logistics_code && (
                      <Box sx={{ fontSize: '12px' }}>
                        <span>{t('logisticsChannel')}</span>
                        <a
                          href={trackMessage.logistics_channel_href}
                          style={{
                            marginLeft: '6px',
                            color: '#463AE8',
                            textDecoration: 'underline',
                          }}
                        >
                          {trackMessage.agent_logistics_code}
                        </a>
                      </Box>
                    )}
                  </Box>
                  {trackMessageArr.map((item, index) => (
                    <Box
                      key={item.id || index}
                      sx={{
                        position: 'relative',
                        padding: '14px 14px 14px 0',
                        borderBottom:
                          index === trackMessageArr.length - 1 ? 'none' : '1px solid #eeeeee',
                        color: index === 0 ? '#333333' : '#666666',
                        lineHeight: '22px',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '-18px',
                          top: '20px',
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          backgroundColor: index === 0 ? '#463AE8' : '#c2c2c2',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '-15px',
                          top: '32px',
                          width: '1px',
                          height: '76%',
                          backgroundColor: '#c2c2c2',
                          display:
                            trackMessageArr.length === 1 || index === trackMessageArr.length - 1
                              ? 'none'
                              : 'block',
                        }}
                      />
                      <Typography sx={{ wordBreak: 'normal' }}>
                        {item.created_at || ''} {item.content}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>
            <Box
              sx={{
                textAlign: 'center',
              }}
            >
              <Link
                prefetch={false}
                href={`/account/my-orders/order-detail/${orderId}?order_no=${orderNo}`}
                className="text-center underline text-primary-purple font-bb cursor-pointer mt-3"
              >
                {t('viewAll')}
              </Link>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            {showLoading ? <CircularProgress size={24} /> : t('noInformation')}
          </Box>
        )}
      </div>
    );
  };
  return (
    <>
      <WebOnly>
        <Popover
          id="track-order"
          triggerComponent={
            <div
              aria-describedby={id}
              className={`text-primary-purple underline cursor-pointer text-14 text-center mb-10 font-br ${className}`}
              onMouseOver={handleClick}
            >
              {t('title')}
            </div>
          }
          sx={{
            zIndex: 99,
          }}
          content={trackOrderContent()}
        />
      </WebOnly>
      <WapOnly>
        <Drawer
          open={openTrackOrderDrawer}
          PaperProps={{
            sx: {
              width: '100%',
              background: colors.auxiliaryLightGray,
            },
          }}
        >
          <div className="h-full w-full relative font-br text-primary-black">
            <div className="fixed top-0 left-0 w-full h-[50px] z-10">
              <MobileNav
                title={t('title')}
                titleClassName="text-center w-full"
                className=" bg-auxiliary-light-gray"
                leftSlot={
                  <div
                    className="MobileNav flex items-center justify-center h-[40px] w-[40px] absolute left-0 top-5"
                    onClick={(event) => {
                      event.stopPropagation();
                      closeTrackOrderDrawer();
                    }}
                  >
                    <WapNavBackOutlined className="text-20" />
                  </div>
                }
              />
            </div>
            <div className="h-[50px]" />
            <div className="h-auto rounded-tl-[2.24vw] rounded-tr-[2.24vw] bg-primary-white ml-16 mr-16">
              <Image
                className="w-full h-auto"
                src="/account/order/wap-shipping-process{locale}.png"
                alt="track-order"
                width={350}
                height={100}
              />
            </div>
            <div className="flex-1 flex-shrink-0 pb-16">
              {showLoading && (
                <div className="text-14 bg-primary-white box-border ml-16 mr-16 h-full overflow-auto rounded-bl-[2.24vw] rounded-br-[2.24vw] flex items-center justify-center">
                  <CircularProgress />
                </div>
              )}
              {!!trackMessage && !showLoading && (
                <div className="text-14 bg-primary-white box-border ml-16 mr-16 h-full overflow-auto rounded-bl-[2.24vw] rounded-br-[2.24vw] pt-16">
                  {!!noticeMessage && <div className="mb-0 mt-4">{noticeMessage}</div>}
                  {!!trackMessage.track_number ||
                    !!trackMessage.shipment_number ||
                    (!!trackMessage.logistics_channel_href &&
                      !!trackMessage.agent_logistics_code && (
                        <div className="mb-20 p-[10px] flex flex-col">
                          {!!trackMessage.track_number ||
                            (!!trackMessage.shipment_number && (
                              <div className="mb-10 leading-[20] flex flex-col">
                                <span className="font-16 font-bb">{t('trackNumber')}</span>
                                <div className="flex items-center justify-between">
                                  <span className="font-16 font-bb">
                                    {trackMessage.track_number || trackMessage.shipment_number}
                                  </span>
                                  <span
                                    className="font-14 text-primary-purple cursor-pointer font-br"
                                    onClick={() => {
                                      copyTrackNumber();
                                    }}
                                  >
                                    {t('copy')}
                                  </span>
                                </div>
                              </div>
                            ))}
                          {!!trackMessage.logistics_channel_href &&
                            !!trackMessage.agent_logistics_code && (
                              <div className="text-12">
                                <span>{t('logisticsChannel')}</span>
                                <Link
                                  prefetch={false}
                                  href={trackMessage.logistics_channel_href}
                                  className="ml-6 text-primary-purple underline"
                                >
                                  {trackMessage.agent_logistics_code}
                                </Link>
                              </div>
                            )}
                        </div>
                      ))}
                  {!!(trackMessage as { country: string }).country && (
                    <p className="pl-10 pb-10">
                      {t('destination')}
                      {(trackMessage as { country: string }).country}
                    </p>
                  )}
                  {/* 尾程 */}
                  {!!trackMessage.order_ships?.length && (
                    <>
                      <h4 className="mb-[16px] ml-[10px] p-[0_6px] w-fit h-[18px] leading-[18px] rounded-[9px] font-br text-white bg-[#bbbbbb]">
                        {t('shippingPhases.after')}
                      </h4>
                      <MoblieTimeLine trackData={trackMessage.order_ships} />
                    </>
                  )}
                  {/* 头程 */}
                  {!!trackMessage.head_ships?.length && (
                    <>
                      <h4 className="mb-[16px] ml-[10px] mt-0 p-[0_6px] w-fit h-[18px] leading-[18px] rounded-[9px] font-br text-white bg-primary-purple">
                        {t('shippingPhases.first')}
                      </h4>
                      <MoblieTimeLine trackData={trackMessage.head_ships} />
                    </>
                  )}
                  {/* 加工信息 */}
                  {!!trackMessage.order_node_val?.length && (
                    <>
                      <h4 className="mb-[16px] ml-[10px] mt-0 p-[0_6px] w-fit h-[18px] leading-[18px] rounded-[9px] font-br text-white bg-primary-purple">
                        {t('shippingPhases.processInformation')}
                      </h4>
                      <MoblieTimeLine trackData={trackMessage.order_node_val} />
                    </>
                  )}
                </div>
              )}
              {!trackMessage && (
                <div className="p-[20px_16px] text-14 text-center">{t('noInformation')}</div>
              )}
            </div>
          </div>
        </Drawer>
      </WapOnly>
    </>
  );
}
